import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Frown, Activity, GitBranchPlus, UserMinus } from 'lucide-react';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import {
  ServiceHero, ProblemsBlock, BuildBlock, ApproachBlock,
  OutcomesBlock, RelatedServices,
} from '@/components/site/ServicePageParts';

gsap.registerPlugin(ScrollTrigger);

const PROBLEMS = [
  { slug: 'clunky-ux', icon: Frown, title: 'Clunky UX', body: 'A phone is held in one hand, in motion, often outdoors. We design for the thumb and the bus stop—not the design review.' },
  { slug: 'poor-performance', icon: Activity, title: 'Poor performance', body: 'Cold start over 2s and your app is uninstalled by Sunday. We profile, budget and ship apps that feel instant on a midrange Indian device.' },
  { slug: 'ios-android-split', icon: GitBranchPlus, title: 'The iOS / Android split', body: 'Two teams, two codebases, two release cadences—chaos. We pick native or RN per feature, not per dogma, and ship one cohesive product.' },
  { slug: 'low-retention', icon: UserMinus, title: 'Low retention', body: 'Most apps lose 75% of users in week one. We design onboarding, notifications and habit loops with retention as the success metric—not downloads.' },
];

const BUILD = [
  { slug: 'native-ios', tag: 'Native · Swift', title: 'Native iOS', body: 'Swift / SwiftUI apps with deep iOS feature use—Live Activities, widgets, App Clips, ARKit when it earns its place.', accent: '#6C5CE7' },
  { slug: 'native-android', tag: 'Native · Kotlin', title: 'Native Android', body: 'Kotlin + Jetpack Compose. We support the Indian device tail (Android 9+) and the entry-level phones your users actually own.', accent: '#4B9FFF' },
  { slug: 'react-native', tag: 'Cross-platform · RN', title: 'React Native / Expo', body: 'One codebase, two stores. Custom native modules when needed. EAS pipelines for fast OTAs.', accent: '#6C5CE7' },
  { slug: 'flutter', tag: 'Cross-platform · Flutter', title: 'Flutter', body: 'When the design language is bespoke and animation-heavy, Flutter buys you craft + speed. We ship to both stores from one Dart codebase.', accent: '#4B9FFF' },
  { slug: 'consumer', tag: 'B2C', title: 'Consumer apps', body: 'D2C, fintech, content, social. Retention-first onboarding, push-cadence design, App Store optimisation built in.', accent: '#4B9FFF' },
  { slug: 'enterprise', tag: 'Enterprise · Field', title: 'Enterprise & field apps', body: 'For sales, last-mile, audit and operations teams. Offline-first sync, role-based capture, MDM-ready builds.', accent: '#6C5CE7' },
];

const APPROACH = [
  { slug: 'strategy', step: '01', title: 'Strategy', body: 'Job-to-be-done, success metric, store-page positioning. We agree on the one number that matters before line one of Swift.' },
  { slug: 'ux', step: '02', title: 'UX', body: 'Thumb-zone flows, dark / light, accessibility, localisation. Indian first names, ₹ in real fields, real network conditions.' },
  { slug: 'prototype', step: '03', title: 'Prototype', body: 'A clickable Figma or a real Expo build on real devices. We test with five users before week three is over.' },
  { slug: 'build', step: '04', title: 'Build', body: 'Two-week sprints. CI builds on every merge. EAS / Fastlane pipelines so internal testers always have today’s app.' },
  { slug: 'device-testing', step: '05', title: 'Device testing', body: 'A bench of 14 real devices—iPhone 11 to 15 Pro, Redmi to Pixel. We profile thermals, jank and battery, not just bugs.' },
  { slug: 'store-launch', step: '06', title: 'Store launch', body: 'Listings, screenshots, copy, video, App Store / Play review prep. We handle the rejections, you handle the press.' },
  { slug: 'aso', step: '07', title: 'ASO', body: 'Keywords, install funnel, A/B store listings, screenshot tests. Discovery isn’t a marketing afterthought—it’s a product surface.' },
  { slug: 'post-launch', step: '08', title: 'Post-launch support', body: 'Crashlytics triage, OTA fixes, a monthly review cadence. We stay through the second data point, not just the first.' },
];

const FAQ = [
  { q: 'Native or cross-platform?', a: 'We pick per app, per feature. Most consumer apps in 2025 are best served by React Native + a couple of native modules. Heavy AR / camera / wearables work native.' },
  { q: 'How long to ship a v1?', a: 'Eight to fourteen weeks depending on scope. A pure RN consumer app with auth, payments and three core flows is usually ten.' },
  { q: 'Do you handle store submission?', a: 'Yes. Listings, screenshots, privacy declarations, review prep, the back-and-forth with the reviewer. You hand us the keys, we hand you a live link.' },
  { q: 'What about offline and push?', a: 'Offline-first sync with conflict resolution if it matters (it usually does). Push via FCM / APNs with proper segmentation and quiet hours.' },
  { q: 'Will the app be ready for low-end Android?', a: 'Yes. Our test bench includes a ₹9k Redmi running Android 10. If it doesn’t fly there, it doesn’t ship.' },
  { q: 'Do you build watchOS / Wear OS / TV?', a: 'When the core app earns it. We’ll usually recommend phone first, then watch as a focused companion.' },
];

const OUTCOMES = [
  { kpi: 4.8, suffix: '/5', label: 'avg. App Store rating' },
  { kpi: 41, suffix: '%', label: 'D30 retention (median)' },
  { kpi: 220, suffix: 'k+', label: 'downloads across portfolio' },
  { kpi: 1.2, suffix: 's', label: 'cold-start on midrange Android' },
];

const STORE_NOTE = [
  { k: 'Store approval', v: 'We handle the Apple + Google review dance. Privacy declarations, demo accounts, age-rating, the lot. First-attempt approvals on the last 6 of 7.' },
  { k: 'Offline & push', v: 'Offline-first sync where it matters. FCM + APNs with segmentation, quiet hours, and proper localisation. No spammy push.' },
  { k: 'OTAs', v: 'EAS Update / CodePush wired in for non-native fixes. Most bugs ship within hours, not the next App Store review.' },
];

/* Signature: phone-frame horizontal showcase */
const SCREENS = [
  {
    title: 'Onboard',
    body: 'Three screens. One ask per screen. Phone number → OTP → name. 84% completion.',
    palette: ['#6C5CE7', '#4B9FFF'],
    chips: ['Phone OTP', 'No password', 'Aadhaar optional'],
  },
  {
    title: 'Home · Today',
    body: 'A focused list of what to do today. No infinite scroll. No tabs across the top.',
    palette: ['#4B9FFF', '#6C5CE7'],
    chips: ['Daily list', 'Streaks', 'Quick add'],
  },
  {
    title: 'Track',
    body: 'A scrollable chart with a draggable cursor. Numbers fade up under the cursor.',
    palette: ['#6C5CE7', '#0A0A0F'],
    chips: ['Live chart', 'Compare', 'Notes'],
  },
  {
    title: 'Profile · Settings',
    body: 'Boring on purpose. Account, billing, privacy, language, theme. Everything in one place.',
    palette: ['#4B9FFF', '#0A0A0F'],
    chips: ['Hindi · EN', 'Dark / light', 'Export data'],
  },
];

function PhoneFrame({ idx, screen, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`mob-screen-${idx}`}
      aria-pressed={active}
      className={`group relative shrink-0 w-[260px] md:w-[300px] rounded-[36px] border transition-all duration-500 ${
        active ? 'border-white/40 -translate-y-2 shadow-[0_30px_80px_-30px_rgba(108,92,231,0.6)]' : 'border-white/[0.12]'
      } bg-[#0E0E15]`}
      style={{ padding: 10 }}
    >
      <div className="relative aspect-[9/19.5] rounded-[28px] overflow-hidden bg-black">
        {/* notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-black border border-white/10 z-20" />
        {/* content */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(circle at 30% 20%, ${screen.palette[0]}55, transparent 55%), radial-gradient(circle at 80% 80%, ${screen.palette[1]}55, transparent 50%), #0A0A0F`,
            }}
          />
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="relative h-full flex flex-col p-5 pt-10 text-left">
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/60">{screen.title}</div>
            <div className="mt-3 text-[18px] font-bold tracking-tight leading-tight text-white">
              {screen.body}
            </div>
            <div className="mt-auto space-y-2">
              {screen.chips.map((c) => (
                <div key={c} className="text-[10px] px-2.5 py-1.5 rounded-full border border-white/[0.15] bg-white/[0.04] inline-block mr-1.5 text-white/85">
                  {c}
                </div>
              ))}
              <div className="pt-3 text-[10px] text-white/40 uppercase tracking-[0.22em]">
                Screen 0{idx + 1} / 0{SCREENS.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function PhoneShowcase() {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  return (
    <div ref={trackRef} data-testid="mob-phone-showcase" className="relative">
      <div className="flex gap-6 overflow-x-auto pb-6 -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory">
        {SCREENS.map((s, i) => (
          <div key={i} className="snap-center first:pl-2 last:pr-2">
            <PhoneFrame idx={i} screen={s} active={active === i} onClick={() => setActive(i)} />
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-ink-muted">
        <span>{SCREENS[active].title}</span>
        <span>Tap a phone to focus</span>
      </div>
    </div>
  );
}

export default function MobileAppDevelopment() {
  return (
    <div data-testid="mobile-app-development-page">
      <ServiceHero
        serviceNum="03"
        serviceTitle="Mobile App Development"
        headlineText="Mobile apps people keep on their home screen."
        accent="screen."
        description="Apps designed for the thumb, the train, and the in-between minutes. Built native or React Native—whichever the feature actually deserves—and shipped to both stores with the boring stuff (offline sync, push, ASO) done right."
        secondaryCta={{ label: 'See app work', to: '/work' }}
        testIdPrefix="mob"
      />

      <ProblemsBlock
        heading="Four reasons apps get deleted in week one."
        accentWord="week one."
        intro="Most app failures are not bug-shaped. They are attention-shaped, performance-shaped or habit-shaped. We fix the right one."
        items={PROBLEMS}
        testIdPrefix="mob"
      />

      <BuildBlock
        heading="Native, cross-platform, or both."
        accentWord="both."
        intro="The right answer is rarely religious. We pick per feature: native where it earns the cost, RN / Flutter where it earns the speed."
        items={BUILD}
        testIdPrefix="mob"
      />

      <ApproachBlock
        heading="Eight stages. One product."
        accentWord="One product."
        intro="From job-to-be-done to ASO, we own the full app surface. No throwing it over the wall to a launch team."
        steps={APPROACH}
        testIdPrefix="mob"
      />

      {/* Signature — horizontal phone showcase */}
      <section data-testid="mob-signature" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-end">
            <div className="md:col-span-7">
              <SectionLabel num="05">Recent · Aarohan Driver</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                Four screens, one <span className="italic text-violet">very-low-friction</span> app.
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-6 md:border-l border-white/10">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-md">
                  Aarohan Driver — a fleet companion app for a Tier-2 hauler. Routing, ePOD, fuel reconciliation. Three taps to start a shift.
                </p>
              </Reveal>
            </div>
          </div>
          <Reveal>
            <PhoneShowcase />
          </Reveal>
        </div>
      </section>

      <OutcomesBlock
        heading="The numbers that matter—after week one."
        accentWord="after week one."
        intro="Medians across the last 8 mobile engagements (2024–25). Downloads alone don’t pay the bills—retention does."
        values={OUTCOMES}
        testIdPrefix="mob"
      />

      {/* Store / offline / push note */}
      <section data-testid="mob-store-note" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="08">Boring, but important</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Store, push, <span className="italic text-electric">offline.</span>
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {STORE_NOTE.map((n, i) => (
              <Reveal key={n.k} delay={i * 90}>
                <div data-testid={`mob-note-${i}`} className="h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">— 0{i + 1}</div>
                  <div className="mt-4 text-lg font-semibold tracking-tight">{n.k}</div>
                  <p className="mt-3 text-ink-muted leading-relaxed text-sm">{n.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="mob-faq" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="09">FAQ</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Things we&apos;re asked <span className="italic text-violet">before</span> the call.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FAQList items={FAQ} testIdPrefix="mob-faq" />
          </div>
        </div>
      </section>

      <RelatedServices exclude="mobile-app-development" testIdPrefix="mob" />

      <CTABand
        eyebrow="App brief on your desk?"
        headline="Bring it. We’ll bring sharper questions."
        sub="A 30-minute call with a partner. No SDR, no deck. Bring a flow you’re unsure of."
      />
    </div>
  );
}
